'use client';

import { type CSSProperties, Children, type ReactElement, forwardRef, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { useChildrenOfType } from '../../hooks/use-children-of-type';
import type { TagPropsInterface } from '../tag';
import { ProgressStepperRootContext } from './context/progress-stepper-root.context';
import { ProgressStepperCurrentLabel } from './progress-stepper-current-label';
import { progressStepperRootRecipe } from './styles/progress-stepper-root.styles';
import type { ProgressStepperRootPropsInterface } from './types/progress-stepper-root.types';
import type { ProgressStepperStepPropsInterface } from './types/progress-stepper-step.types';
import type { ProgressStepperStepsPropsInterface } from './types/progress-stepper-steps.types';

const DEFAULT_STEPS: ProgressStepperRootPropsInterface['steps'] = [];
const DEFAULT_LABEL_APPEARANCE: ProgressStepperRootPropsInterface['labelAppearance'] = {
  base: 'current',
  md: 'all',
};

interface CurrentLabelType {
  label: string;
  meta: string | ReactElement<TagPropsInterface> | null;
}

function getStepProps(
  step: NonNullable<ProgressStepperRootPropsInterface['steps']>[number],
  mapStepsToProps?: ProgressStepperStepsPropsInterface['mapStepsToProps'],
): ProgressStepperStepPropsInterface {
  if (mapStepsToProps) {
    return mapStepsToProps(step);
  }

  return typeof step === 'string' ? { label: step } : step;
}

function getActiveComposedStep(
  stepElements: Array<ReactElement<ProgressStepperStepPropsInterface>>,
  activeStepIndex: number,
): ProgressStepperStepPropsInterface | undefined {
  for (let i = stepElements.length - 1; i >= 0; i--) {
    const stepProps = stepElements[i]!.props;

    if (stepProps.index === activeStepIndex || stepProps.active) {
      return stepProps;
    }
  }
}

/**
 * ProgressStepper Root component
 *
 * @since 10.0.0
 */
export const ProgressStepperRoot = forwardRef<HTMLDivElement, ProgressStepperRootPropsInterface>(
  (props, forwardedRef) => {
    const {
      activeStepIndex = 0,
      alignLabels = 'center',
      'aria-label': ariaLabel,
      children,
      className,
      css: cssProp,
      labelAppearance = DEFAULT_LABEL_APPEARANCE,
      mapStepsToProps,
      onStepClick,
      orientation = 'horizontal',
      steps = DEFAULT_STEPS,
      style,
      ...rest
    } = props;

    const styles = css(progressStepperRootRecipe.raw({ orientation }), cssProp);
    const stepContainers = useChildrenOfType(children, 'ProgressStepper.Steps');
    const composedSteps = useChildrenOfType(children, 'ProgressStepper.Step') as Array<
      ReactElement<ProgressStepperStepPropsInterface>
    >;

    const count = useMemo(() => {
      if (steps.length > 0) {
        return steps.length;
      }

      const explicitTotalSteps = stepContainers.find(
        (stepContainer) => typeof stepContainer.props.totalSteps === 'number',
      )?.props.totalSteps;

      if (typeof explicitTotalSteps === 'number') {
        return explicitTotalSteps;
      }

      const stepContainerChildrenCount = stepContainers.reduce((total, stepContainer) => {
        return total + Children.toArray(stepContainer.props.children).length;
      }, 0);

      return stepContainerChildrenCount || composedSteps.length;
    }, [composedSteps.length, stepContainers, steps.length]);

    const activeLabel = useMemo<CurrentLabelType>(() => {
      const stepsMap = stepContainers[0]?.props.mapStepsToProps || mapStepsToProps;

      if (steps.length > 0) {
        const activeStep = steps[activeStepIndex];

        if (activeStep) {
          const stepProps = getStepProps(activeStep, stepsMap);
          return {
            label: stepProps.label ?? '',
            meta: stepProps.meta ?? null,
          };
        }

        return { label: '', meta: null };
      }

      const activeStep = getActiveComposedStep(composedSteps, activeStepIndex);

      return {
        label: activeStep?.label ?? '',
        meta: activeStep?.meta ?? null,
      };
    }, [activeStepIndex, composedSteps, mapStepsToProps, stepContainers, steps]);

    const inlineStyles = useMemo(() => {
      return {
        '--c11n-steps': `${count}`,
        '--c11n-step-width': `${(1 / count) * 100}%`,
        ...style,
      } as CSSProperties;
    }, [style, count]);

    const contextValue = useMemo(
      () => ({
        activeStepIndex,
        alignLabels,
        steps,
        labelAppearance,
        mapStepsToProps,
        onStepClick,
        orientation,
      }),
      [
        activeStepIndex,
        alignLabels,
        steps,
        labelAppearance,
        mapStepsToProps,
        onStepClick,
        orientation,
      ],
    );

    return (
      <ProgressStepperRootContext.Provider value={contextValue}>
        <section
          aria-label={ariaLabel}
          className={cx('c11n', 'c11n_progress-stepper__root', styles, className)}
          data-c11n-component="ProgressStepper.Root"
          data-c11n-version={__C11N_VERSION__}
          data-orientation={orientation}
          ref={forwardedRef}
          style={inlineStyles}
          {...rest}
        >
          {children}
          <ProgressStepperCurrentLabel label={activeLabel.label} meta={activeLabel.meta} />
        </section>
      </ProgressStepperRootContext.Provider>
    );
  },
);

ProgressStepperRoot.displayName = 'ProgressStepper.Root';
